import React, { useEffect } from 'react'
import Text from '@embracesbs/component-text'
import Table from '@embracesbs/component-table'
import TableRow from '@embracesbs/component-tablerow'
import Checkbox from '@embracesbs/component-checkbox'
import Button2 from '@embracesbs/component-button2'
import Container from '@embracesbs/component-container'
import TextField from '@embracesbs/component-textfield'
import { FeatureFlags } from '../../../Interfaces/Types'
import { sortByLabel } from '../../../Utilities/helpers/helpers'
import { useTenants } from '../../Context/TenantContext'
import Toast, { customToast } from '@embracesbs/component-toast'
import { useApi, useAuth } from '../../../Hooks'

const style = {
  maxHeight: '90%', // Ensures that the div doesn't grow beyond the outer div's height
  overflow: 'auto', // Enables scrolling
  paddingRight: '15px'
}

const FeatureFlag: React.FC = () => {
  const { selectedTenantId } = useTenants()
  const [featureFlags, setFeatureFlags] = React.useState<FeatureFlags[] | undefined>(undefined)
  const [changedFlags, setChangedFlags] = React.useState<FeatureFlags[]>([])
  const [query, setQuery] = React.useState('')
  const { fetchApi } = useApi()
  const { accessToken } = useAuth()

  const toggleFeatureFlag = (name: string) => {
    if (!featureFlags) return

    const updatedFlags = featureFlags.map((flag) => {
      if (flag.name === name) {
        return { ...flag, enabled: !flag.enabled }
      }
      return flag
    })

    const changedFlag = updatedFlags.find((flag) => flag.name === name)
    if (!changedFlag) return

    setFeatureFlags(updatedFlags)
    setChangedFlags((prev) => {
      const existing = prev.findIndex((f) => f.name === name)
      if (existing >= 0) {
        return prev.filter((f) => f.name !== name)
      }
      return [...prev, changedFlag]
    })
  }

  const saveChanges = async () => {
    if (changedFlags.length === 0) return

    try {
      await fetchApi({
        endpoint: `/api/featureflags/${selectedTenantId}`,
        method: 'PUT',
        body: changedFlags
      })

      customToast.success('Feature flags have been updated')
      setChangedFlags([])
    } catch (error) {
      console.error(error)
      customToast.error('Failed to update feature flags')
    }
  }

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      fetchApi({
        endpoint: `/api/featureflags/${selectedTenantId}?forceNoCache=true`,
        method: 'GET'
      })
        .then((response) => {
          console.log('fetched feature flags')
          if (response !== undefined && response.length > 0) {
            const sortedFlags = response.sort(sortByLabel)
            setFeatureFlags(sortedFlags)
            setChangedFlags([])
          }
        })
        .catch((error) => {
          console.error(error)
          customToast.error('Failed to fetch feature flags')
        })
    }

    if (selectedTenantId) {
      fetchFeatureFlags()
    }
  }, [selectedTenantId, accessToken])

  if (!selectedTenantId) {
    return (
      <Text align='center' textColor='grey'>
        Please select a tenant
      </Text>
    )
  }

  const normalizedQuery = query.trim().toLowerCase()
  const visibleFlags = normalizedQuery
    ? featureFlags?.filter(
        (flag) =>
          flag.name?.toLowerCase().includes(normalizedQuery) ||
          flag.label?.toLowerCase().includes(normalizedQuery) ||
          flag.description?.toLowerCase().includes(normalizedQuery)
      )
    : featureFlags

  return (
    <>
      <Container justify='space-between' isFullWidth style={{ gap: '12px' }}>
        <div className='search-no-glow' style={{ flex: 1, maxWidth: '420px' }}>
          <TextField
            isSearchMode
            placeholder='Search feature flags'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            type='text'
            variant='default'
          />
        </div>
        <Button2
          text={`Save Changes (${changedFlags.length})`}
          onClick={saveChanges}
          isDisabled={changedFlags.length === 0}
          variant='contained'
        />
      </Container>
      <div style={style}>
        <Toast />

        {visibleFlags?.length === 0 && (
          <Text align='center' textColor='grey'>
            No feature flags match '{query}'
          </Text>
        )}
        <Table>
          {visibleFlags?.map((item, index) => (
            <TableRow
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: changedFlags.some((f) => f.name === item.name) ? 'var(--tma-row-hover)' : 'transparent',
                marginBottom: '0.5rem'
              }}
              hasHover={false}
            >
              <Text style={{ minWidth: '100px' }}>{item.label}</Text>
              <Checkbox
                name={item.name}
                label={item.name}
                isChecked={item.enabled}
                isDisabled={!item.hasTenantFilter}
                description={item.description}
                onClick={() => toggleFeatureFlag(item.name)}
                value='a'
                isDescriptionSingleLine={true}
                isLabelSingleLine={true}
                innerSpacing={0}
              />
            </TableRow>
          ))}
        </Table>
      </div>
    </>
  )
}

export default FeatureFlag
